import { useCallback, useState, type ReactNode } from 'react';
import { fosApi, fosError, isFinancialAckRequired } from '../../lib/fosApi';
import FinancialAckModal from './FinancialAckModal';

/**
 * Financial information is delivered only after the user has acknowledged, once per council-local day, that their access is logged.
 * The SERVER enforces that: it refuses the first financial read or write of the day and delivers/changes nothing. `guard` runs a
 * request; if the server asks for the acknowledgment it shows the dialog and, once the user agrees, repeats the SAME request.
 * Cancelling leaves the original refusal for the caller to show.
 */
export function useFinancialGuard(): { guard: <T>(run: () => Promise<T>) => Promise<T>; modal: ReactNode } {
  const [pending, setPending] = useState<{ resume: () => void; cancel: () => void } | null>(null);
  const guard = useCallback(async <T,>(run: () => Promise<T>): Promise<T> => {
    try { return await run(); } catch (cause) {
      if (!isFinancialAckRequired(fosError(cause))) throw cause;
      return new Promise<T>((resolve, reject) => {
        setPending({ resume: () => { run().then(resolve, reject); }, cancel: () => reject(cause) });
      });
    }
  }, []);
  const modal = pending ? (
    <FinancialAckModal
      acknowledge={(version) => fosApi.acknowledgeFinancialAccess({ textVersion: version })}
      onAcknowledged={() => { const p = pending; setPending(null); p.resume(); }}
      onCancel={() => { const p = pending; setPending(null); p.cancel(); }}
    />
  ) : null;
  return { guard, modal };
}
